import { Select, Button } from "@rio-cloud/rio-uikit";
import DatePicker from "@rio-cloud/rio-uikit/components/datepicker/DatePicker";

import { getOccurrenceStepLabelByStepValue, OccurrenceStepEnum } from "../../../enums/occurrenceStep";
import { Loading } from "../../../utils/loading";
import { useKanbanWorkLog } from "../../../hooks/useWorkLog";
import { KanbanWorkLog, KanbanWorkLogFieldChange } from "../../../models/workLog";
import { fieldsWorklogLabels } from "./form/utils/fields-labels";
import { formatStringToReadableValue } from "../../../utils/stringUtils";
import { formatDateTimeWithTimezone } from "../../../utils/dateUtils";

const Worklog = ({ occurrenceId }: { occurrenceId: string }) => {

  const { workLogs, loading, loadMore, hasMoreData, filters, setFilters, resetPage } = useKanbanWorkLog(occurrenceId);
  
  const getFieldLabel = (fieldName: string): string => {
    return fieldsWorklogLabels[fieldName as keyof typeof fieldsWorklogLabels] || fieldName;
  };

  const getBooleanValue = (value: string): string => {
    switch(value) {
      case 'true':
        return 'Sim';
      case 'yes':
        return 'Sim';
      case 'y':
        return 'Sim';
      case 'no':
        return 'Não';
      case 'n':
        return 'Não';
      case 'false':
        return 'Não';
      default:
        return value;
    }
  };

  const logItem = (log: KanbanWorkLog, fieldChange: KanbanWorkLogFieldChange | undefined, hasFieldChanges: boolean) => {
    return (
      <div key={`${log.id}-${fieldChange?.id ?? 0}`} className="flex items-start justify-between padding-left-15 padding-bottom-40">
        <span className="w-1/12 text-size-18 bg-gray-100 rounded-circle width-30 height-30 flex items-center justify-center rioglyph rioglyph-ok mt-2"></span>

        <div className="w-full flex flex-col border border-gray-200 ml-6 p-4 rounded-lg">
          <div className="w-full flex flex-row items-start">
            <div className="w-2/12 flex flex-col padding-left-10 border-r">
              <span className="font-bold">Usuário:</span>
              <span className="truncate">{log.userName}</span>
            </div>

            <div className="w-2/12 flex flex-col padding-left-10 border-r">
              <span className="font-bold">Data/Hora:</span>
              <span className="truncate">
                {formatDateTimeWithTimezone(log.changedAt)}
              </span>
            </div>

            <div className="w-2/12 flex flex-col padding-left-10 border-r">
              <span className="font-bold">Etapa:</span>
              <span className="truncate">{getOccurrenceStepLabelByStepValue(log.step)}</span>
            </div>

            <div className="w-6/12 flex flex-col padding-left-10">
              <span className="font-bold">{hasFieldChanges ? `Campo alterado` : `Descrição`}:</span>
              <span className="truncate">
                {hasFieldChanges
                  ? fieldChange?.fieldName === 'comment'
                    ? log.description
                    : getFieldLabel(fieldChange?.fieldName ?? '')
                  : log.description}
              </span>
            </div>
          </div>

            <div className="w-full flex flex-row mt-4">
              <div className="w-6/12 flex flex-col padding-left-10 border-r">
                <span className="font-bold">Valor anterior: </span>
                <div className="bg-gray-50 rounded-lg p-4 mr-4 break-words whitespace-pre-wrap">
                  {hasFieldChanges ? formatStringToReadableValue(getBooleanValue(fieldChange?.oldValue ?? '-')) : '-'}
                </div>
              </div>

              <div className="w-6/12 flex flex-col padding-left-10">
                <span className="font-bold">Novo valor:</span>
                <div className="bg-gray-50 rounded-lg p-4 mr-4 break-words whitespace-pre-wrap">
                  {hasFieldChanges ? formatStringToReadableValue(getBooleanValue(fieldChange?.newValue ?? '-')) : '-'}
                </div>
              </div>
            </div>
        </div>
      </div>
    )
  }

  return (
    <div className="history-assistance w-full">
      <div className="text-size-h2 font-medium">Log de atualizações</div>
      <div className="text-size-h5 mb-8">Dados que foram alterados neste chamado</div>

      <div className="filter-row flex flex-row items-end gap-6 mb-6">
        <div className="flex flex-col">
          <label htmlFor="step" className="mb-1 text-gray-700">Etapa</label>
          <Select
            id="step"
            className="min-w-[180px] max-w-[220px]"
            placeholder="Todas"
            options={[
              { id: '', label: 'Todas' },
              ...Object.values(OccurrenceStepEnum).map((step) => ({
                id: step,
                label: getOccurrenceStepLabelByStepValue(step),
              })),
            ]}
            onChange={(option) => 
              setFilters({ ...filters, step: option?.id ?? '' })}
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="user" className="mb-1 text-gray-700">Usuário</label>
          <input
            id="user"
            type="text"
            className="min-w-[180px] max-w-[220px]"
            placeholder="Digite o nome"
            onChange={(e) => 
              setFilters({ ...filters, user: e.target.value })}
          />
        </div>

        <div className="flex flex-col relative top-[14px]">
          <label className="mb-1 text-gray-700">Data atualização</label>
          <div className="flex flex-row items-center gap-2">
            <DatePicker
              className="w-[200px] date-picker"
              id="update-date-start"
              locale="pt-BR"
              dateFormat="DD/MM/yyyy"
              onChange={(date) =>
                setFilters({ ...filters, dateStart: typeof date === 'string' ? date : date?.toDate()?.toISOString() ?? '',
              })
            }
              timeFormat="HH:mm"
      />
      <span className="text-gray-700">Até</span>
      <DatePicker
        className="w-[200px] date-picker"
        id="update-date-end"
        locale="pt-BR"
        dateFormat="DD/MM/yyyy"
          onChange={(date) => setFilters({ ...filters, dateEnd: typeof date === 'string' ? date : date?.toDate()?.toISOString() ?? '',})
        }
        timeFormat="HH:mm"
      />
    </div>
  </div>
        <div className="flex items-end">
          <Button
            className="min-w-[100px]"
            type="button"
            onClick={() => resetPage()}
          >
            Aplicar filtro
          </Button>
        </div>
      </div>

      <>
        {loading ? (
          <div className="flex items-center justify-center mt-[80px]">
            <Loading label="Carregando log de atualizações..." />
          </div>
        ) : (
          <section className="padding-top-20 vertical-divider">
            {(workLogs ?? []).map((log: KanbanWorkLog, logIndex: number) => {
              const hasFieldChanges = log.fieldChanges && log.fieldChanges.length > 0;
              if(hasFieldChanges) {
                return log.fieldChanges.map((fieldChange: KanbanWorkLogFieldChange, fieldIndex: number) => (
                  logItem(log, fieldChange, hasFieldChanges)
                ));
              } else {
                return logItem(log, undefined, hasFieldChanges);
              }
            })}

            {hasMoreData && <div
              className="LoadMore cursor-pointer text-center py-8 text-color-primary"
              onClick={() => loadMore()}
            >Carregar mais</div>}
          </section>
        )}
      </>
    </div>
  );
};

export default Worklog;
